import React, { useState, useCallback } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';
import validator from 'validator';
import { artistState, loadingState, toastState } from '../state/RecoilState';
import RestUtilities from '../services/RestUtilities';
import { blobUrl, contentConfig, uploadType } from '../Constants';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Grid,
  Button,
  Box,
  TextField,
  useTheme,
  Container,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import MfsRichTextEditor from '../components/Common/RichText/RichTextEditor';
import ModalFileManager from '../components/Common/ModalFileManager';
import MustPickArtist from '../components/Common/MustPickArtist';
import { ContentItem, ContentType } from '../@types/api';

const SatelliteContent: React.FC = () => {
  const theme = useTheme();
  const setToast = useSetRecoilState(toastState);
  const artist = useRecoilValue(artistState);
  const setLoading = useSetRecoilState(loadingState);
  const [show, setShow] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<
    ContentItem | null | undefined
  >(undefined);
  const [hover, setHover] = useState<boolean>(false);
  const [editor, setEditor] = useState<any>(undefined);
  const [isTouched, setIsTouched] = useState(false);

  const loadData = useCallback(
    async (pageId: string) => {
      setLoading(true);
      if (!artist) return;
      const artistId = artist.artistId;
      const url = `Api/PageContent/${artistId}/${pageId}`;
      const response = await RestUtilities.get(url);
      if (response.ok) {
        const dbPage = response.content;
        const currentPage: ContentItem | undefined = contentConfig.find(
          (x) => x.name === pageId
        );
        if (currentPage) {
          currentPage.id = dbPage.pageId;
          const pageImage = currentPage.content.find(
            (x) => x.name === 'pageImage'
          );
          const pageTitle = currentPage.content.find(
            (x) => x.name === 'pageTitle'
          );
          const mainContent = currentPage.content.find(
            (x) => x.name === 'mainContent'
          );
          const songlist = currentPage.content.find(
            (x) => x.name === 'songlist'
          );
          const showcase = currentPage.content.find(
            (x) => x.name === 'showcase'
          );
          const contactUs = currentPage.content.find(
            (x) => x.name === 'contactUs'
          );

          if (mainContent) mainContent.value = dbPage.pageContent;
          if (songlist) songlist.value = dbPage.songlistText;
          if (showcase) showcase.value = dbPage.showcaseText;
          if (contactUs) contactUs.value = dbPage.contactUsText;
          if (pageTitle) pageTitle.value = dbPage.pageTitle;
          if (pageImage) pageImage.value = dbPage.pageImage;
        }
        setToast('Loaded');
        setCurrentPage(currentPage);
        setLoading(false);
        setIsTouched(false);
      }
    },
    [artist, setLoading, setToast]
  );

  const toggleContentType = (c: ContentItem) => {
    loadData(c.name);
    setIsTouched(false);
  };

  const handleClose = () => {
    setShow(false);
  };

  const handleUploadSelect = (upload: { fileName: string }) => {
    const content = currentPage?.content.find(
      (x: any) => x.name === 'pageImage'
    );
    if (content) {
      content.value = upload.fileName;
      setCurrentPage(currentPage);
      setShow(false);
      setIsTouched(true);
    }
  };

  const handleTextAreaChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    contentId: string
  ) => {
    const content = currentPage?.content.find((x) => x.name === contentId);
    if (content) {
      const text = validator.escape(e.currentTarget.value);
      content.value = text;
      setCurrentPage({ ...currentPage } as ContentItem);
      setIsTouched(true);
    }
  };

  const onUpdate = async () => {
    setLoading(true);
    const mainContent = editor?.getHTML();
    const pageImage = currentPage?.content.find(
      (x: any) => x.name === 'pageImage'
    );
    const pageTitle = currentPage?.content.find(
      (x: any) => x.name === 'pageTitle'
    );
    const songlist = currentPage?.content.find(
      (x: any) => x.name === 'songlist'
    );
    const showcase = currentPage?.content.find(
      (x: any) => x.name === 'showcase'
    );
    const contactUs = currentPage?.content.find(
      (x: any) => x.name === 'contactUs'
    );

    const content = {
      pageId: currentPage?.id,
      pageName: currentPage?.name ?? '',
      pageTitle: pageTitle?.value ?? '',
      pageContent: mainContent ?? '',
      pageImage: pageImage?.value,
      artistId: artist?.artistId,
      songlistText: songlist?.value ?? null,
      showcaseText: showcase?.value ?? null,
      contactUsText: contactUs?.value ?? null,
    };

    const url = `api/PageContent/${artist?.artistId}/${currentPage?.id}`;
    const response = await RestUtilities.put(url, content);

    if (response.ok && currentPage) {
      currentPage.id = response.content.pageId;
      setCurrentPage(currentPage);
      setToast('Updated');
    }
    setLoading(false);
    setIsTouched(false);
  };

  const renderContentSelector = () => {
    return (
      <FormControl>
        <Typography variant='subtitle1' color={theme.palette.text.secondary}>
          Select Content Type
        </Typography>
        <RadioGroup
          row
          aria-labelledby='buttons-group-label'
          name='row-radio-buttons-group'
        >
          {contentConfig.map((c: ContentItem, i) => (
            <FormControlLabel
              key={c.name}
              control={<Radio />}
              label={c.displayName}
              checked={c.name === currentPage?.name}
              onChange={() => toggleContentType(c)}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const renderEditors = () => {
    if (!currentPage) return null;
    return currentPage.content.map((c: ContentType) => (
      <Box key={c.name}>{renderEditor(c)}</Box>
    ));
  };

  const renderEditor = (c: ContentType) => {
    switch (c.type) {
      case 'textarea':
        return renderTextArea(c);
      case 'richtext':
        return renderRichTextEditor(c);
      case 'image':
        return renderHeaderImage(c);
      default:
        return null;
    }
  };

  const renderHeaderImage = (c: ContentType) => {
    const bg = c.value ? `url('${blobUrl}/images/${c.value}')` : 'black';
    const showMask = hover || !c.value;
    return (
      <>
        <Typography variant='subtitle1' color={theme.palette.text.secondary}>
          {c.displayName}
        </Typography>
        <Box
          className='photo-container'
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          sx={{
            position: 'relative',
            mb: 2,
            minHeight: 100,
            backgroundColor: '#666',
          }}
        >
          {showMask && (
            <Box sx={maskStyles}>
              <Edit
                onClick={() => setShow(true)}
                sx={{
                  color: 'white',
                  cursor: 'pointer',
                  display: showMask ? 'block' : 'none',
                }}
              />
            </Box>
          )}
          {c.value && <Box style={{ ...bgImageStyles, background: bg }} />}
        </Box>
      </>
    );
  };

  const renderTextArea = (c: ContentType) => {
    return (
      <FormControl key={c.name} fullWidth>
        <TextField
          multiline
          label={c.displayName}
          value={c.value || ''}
          fullWidth
          margin='normal'
          placeholder={c.displayName}
          onChange={(e) => handleTextAreaChange(e, c.name)}
          InputProps={{
            rows: 3,
            multiline: true,
            inputComponent: 'textarea',
          }}
          variant='outlined'
        />
      </FormControl>
    );
  };

  const renderRichTextEditor = (c: ContentType) => {
    return (
      <Box sx={{ my: 1, pb: 3 }} key={c.name}>
        <Typography variant='subtitle1' color={theme.palette.text.secondary}>
          {c.displayName}
        </Typography>
        <MfsRichTextEditor
          setEditor={(editor) => setEditor(editor)}
          content={c.value ?? ''}
          setTouched={() => setIsTouched(true)}
          isSatellite
        />
      </Box>
    );
  };

  if (!artist) {
    return <MustPickArtist />;
  }
  return (
    <Container maxWidth='lg'>
      <Box display='flex' alignItems='center'>
        <Typography variant='h4'>
          {artist.artistName} Website Content
        </Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={onUpdate}
          size='small'
          disabled={!isTouched}
          sx={{ ml: 3 }}
        >
          Save Changes
        </Button>
      </Box>
      <Grid container>
        <Grid item xs={12}>
          {renderContentSelector()}
        </Grid>
        <Grid item xs={12}>
          {renderEditors()}
        </Grid>
      </Grid>
      <ModalFileManager
        show={show}
        handleClose={handleClose}
        fileType={uploadType.image}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default SatelliteContent;

const maskStyles = {
  position: 'absolute',
  height: '100%',
  width: '100%',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0,0,0,0.3)',
};
const bgImageStyles = {
  width: '100%',
  height: '300px',
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundColor: '#999',
  zIndex: 99,
};
